import { cn } from '@hbs-us/ui';

import { useAccess } from '../../auth/useAccess.js';
import { PunchTiles } from '../timesheets/PunchTiles.js';

import { CrewChecklists } from './components/CrewChecklists.js';
import { CrewWeek } from './components/CrewWeek.js';
import { HomeGreeting } from './components/HomeGreeting.js';

/**
 * Home, for somebody who works in the field rather than reads the money.
 *
 * WHAT IT IS FOR. A crew member opened Home and was sent straight back out to `/timesheets`,
 * because the only Home that existed was the KPI dashboard behind `reports.view`. They now get a
 * Home of their own: the clock they came to punch, the hours it has booked, and the checklists
 * still sitting on them.
 *
 * THE CLOCK LEADS, and it IS the screen. DJ chose the punch screen as the crew home over a
 * dashboard with the clock one click away, so `PunchTiles` sits at the top and everything else
 * reports to it. It reuses the shipped punch hooks, the site combobox and both confirmation
 * dialogs, so the duplicate-site guard and the punch timeout are the same code the Timesheets
 * screen runs — a second punch path would be a second place for a punch to go wrong.
 *
 * EVERY CARD IS GATED BY THE RULE ITS OWN ENDPOINT APPLIES, not by the role that usually holds it.
 * A tenant may rewrite the Employee preset, so a card whose gate is assumed is a card that answers
 * 403 for somebody. Hiding a card is UX; the api worker refuses the data independently.
 */
export function CrewHomeView() {
  const canAccess = useAccess();
  // `qc.view` is in the default Employee preset, and a tenant may take it away. `time.view` is not
  // checked here: it is what selected this layout in the first place (see resolveHomeLayout).
  const showChecklists = canAccess({ permission: 'qc.view' });

  return (
    <div className="min-h-full bg-background px-4 py-6 sm:px-6 lg:px-8">
      <HomeGreeting />

      <PunchTiles />

      {/*
        The two cards sit UNDER the clock, never beside it. The punch is why somebody opened this
        screen, and a card sharing its row would compete for the first look.

        Two columns become one when there is only one card, so the week never sits in a half-width
        column with an empty half beside it. Both cards stretch to the taller of the two, so their
        headings and their links sit on the same lines.
      */}
      <div className={cn('mt-5 grid gap-5', showChecklists && 'lg:grid-cols-2')}>
        <CrewWeek />
        {showChecklists ? <CrewChecklists /> : null}
      </div>

      {/*
        TODO(blocked on GET /api/v1/dispatch/mine?week=): "My jobs this week".

        There is no card here because there is no route to build one from, and no shape to guess at.
        `GET /api/v1/dispatch` is gated on `jobs.view`, which an Employee does not hold, and
        `GET /api/v1/me` returns no `employeeId`, so the SPA cannot filter the tenant board down to
        one person either. It needs the self-scoped route above — gated `time.clock`, employee
        resolved server-side, never from a client-supplied id — and this card lands the day it ships.
      */}
    </div>
  );
}
